import type { PropsWithChildren, FunctionComponent } from 'react'
import { useEffect, useState, useMemo } from 'react'
import { matchPath } from 'react-router'
import { routes } from '@retailer-platform/domain-instacart-developer-platform'
import ErrorBoundary from '../../../legacy/components/ErrorBoundary'
import FeatureEducationModal from '../../../legacy/components/FeatureHighlights/FeatureEducationModal'
import useFeatureEducation from '../../../legacy/components/FeatureHighlights/useFeatureEducation'
import { AppContentContainer } from '../../../gin-and-tonic/containers/app-content-container/AppContentContainer'
import { UserFeedbackButton } from '../../../gin-and-tonic/containers/user-feedback/UserFeedbackButton'
import { LeftShortcutsPaneRetailer } from '../../../gin-and-tonic/containers/nav-v2/components/LeftShortcutsPane/LeftShortcutsPaneRetailer'
import { MenuBarTop } from '../../../gin-and-tonic/containers/nav-v2/components/MenuBarTop'
import { useNavHierarchyRetailer } from '../../../gin-and-tonic/containers/nav-v2/NavMenuHierarchy'
import { useWindowSizeIsWide } from '../../../utils/window/windows.hooks'
import { ContactHeader } from '../../utils/ContactHeader'
import { DASH_NAV_V2_HEIGHT } from '../../../legacy/common/theme'
import { useNavContext } from '../../../gin-and-tonic/containers/nav/utils/NavContext'
import { DashRoute } from '../../../legacy/dash-components/dash-route/DashRoute'
import { DashSwitch } from '../../../legacy/dash-components/dash-switch/DashSwitch'
import { DashRedirect } from '../../../legacy/dash-components/dash-redirect/DashRedirect'
import { HaltTerms } from '../../../sections/halt/components/terms/HaltTerms'
import { usePartnerContext, URL_PARTNER_HALT_TERMS } from '../../../exports/routing'
import { url } from '../../../utils/parsing/url'
import { AgreementTypeEnum } from '../../../__codegen__/api'
import { useCurrentAccountContext } from '../../../utils/current-account/CurrentAccountContext'
import { AttachPartnerAnalyticsMetadata } from '../../utils/AttachAnalyticsMetadata'
import { PartnerType } from '../../../exports/utils'
import {
  consumeStoredRedirectUrl,
  storeCurrentRedirectUrl,
  TERMS_REDIRECT_KEY,
} from '../utils/loggedIn.utils'
import { URL_DASHBOARD } from '../../../utils/routing/routes'
import { AiPageLayout } from '../../../utils/contexts/ai/AiPageLayout'
import { RetailerScopedSection } from './retailer-scoped/RetailerScopedSection'
import { PartnerScopedRoutes } from './PartnerScopedRoutes'

/**
 * Isolated component for the top nav bar.
 * Owns useNavHierarchyRetailer() so its context subscriptions
 * (CoreContext, ThemeContext, ICPlatformContext, etc.) don't
 * cause PartnerScopedApp to re-render.
 */
const PartnerScopedNavBar: FunctionComponent<{ isNavVisible: boolean }> = ({ isNavVisible }) => {
  const { navMenuHierarchy } = useNavHierarchyRetailer()

  if (!isNavVisible) return null

  return <MenuBarTop navMenuHierarchy={navMenuHierarchy} />
}

/**
 * Isolated component for feature education modals.
 * Owns useFeatureEducation() so its context subscriptions
 * (ICPlatformContext, ReactQueryContext, etc.) don't
 * cause PartnerScopedApp to re-render.
 */
const PartnerScopedFeatureEducation: FunctionComponent<{ pathname: string }> = ({ pathname }) => {
  const { isOpen, onOpen, onClose, onClick, feature } = useFeatureEducation(pathname)

  useEffect(() => {
    if (isOpen) {
      onOpen()
    }
  }, [isOpen, onOpen, pathname])

  return (
    <FeatureEducationModal isOpen={isOpen} onClose={onClose} onClick={onClick} feature={feature} />
  )
}

interface Props {
  pathname: string
}

export const PartnerScopedApp: FunctionComponent<PropsWithChildren<Props>> = ({ pathname }) => {
  const isWide = useWindowSizeIsWide()
  const { isNavVisible, isSideNavVisible } = useNavContext()
  const partnerContext = usePartnerContext()

  const { account, refetch } = useCurrentAccountContext()
  const [redirectUrl] = useState<string | null>(() => consumeStoredRedirectUrl(TERMS_REDIRECT_KEY))

  // temporarily gate behind roulette
  const showIdpTerms = !!account.featureToggles?.find(feature => feature.name === 'rt_idp_terms')

  const showDashboardTerms = !!account.featureToggles?.find(
    feature => feature.name === 'rt_dashboard_terms'
  )

  const expectedPartnerAgreementType =
    partnerContext.partnerType === PartnerType.IDP
      ? AgreementTypeEnum.IdpV1
      : AgreementTypeEnum.PartnerDashboardV1

  const termsAccepted = useMemo(
    () =>
      account.acceptedPartnerAgreements.some(
        agreement => agreement.agreementType === expectedPartnerAgreementType
      ),
    [account.acceptedPartnerAgreements, expectedPartnerAgreementType]
  )

  const isImpersonationSession = account.isImpersonating

  const shouldRedirectToTerms = useMemo(
    () =>
      ((showIdpTerms && partnerContext.partnerType === PartnerType.IDP) ||
        (showDashboardTerms && partnerContext.partnerType === PartnerType.Retailer)) &&
      !termsAccepted &&
      !isImpersonationSession &&
      !matchPath(pathname, { path: URL_PARTNER_HALT_TERMS }),
    [
      showIdpTerms,
      showDashboardTerms,
      partnerContext.partnerType,
      termsAccepted,
      pathname,
      isImpersonationSession,
    ]
  )

  const shouldConsumeTermsRedirectUrl = useMemo(
    () => matchPath(pathname, { path: URL_PARTNER_HALT_TERMS }) && termsAccepted,
    [pathname, termsAccepted]
  )

  // Wait for partner data before rendering — inlined from useRequiredNavDataLoadingStatus()
  // to avoid an extra PartnerContext subscription from that hook
  if (!partnerContext.partner) {
    return null
  }

  if (shouldRedirectToTerms) {
    storeCurrentRedirectUrl(TERMS_REDIRECT_KEY)
    return (
      <DashRedirect
        to={`${url(URL_PARTNER_HALT_TERMS, {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          partner_id: partnerContext.partnerId,
        })}`}
      />
    )
  }

  if (shouldConsumeTermsRedirectUrl) {
    const defaultUrl =
      partnerContext.partnerType === PartnerType.IDP
        ? url(routes['instacart-developer-platform-get-started'], {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            partner_id: partnerContext.partnerId,
          })
        : url(URL_DASHBOARD, {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            partner_id: partnerContext.partnerId,
          })

    return <DashRedirect to={redirectUrl || defaultUrl} />
  }

  const topPadding = isNavVisible ? DASH_NAV_V2_HEIGHT : '0px'
  const leftPadding = '0px'

  return (
    <>
      <DashSwitch>
        <DashRoute
          route="partner-halt-terms"
          render={() => <HaltTerms refetchCurrentAccount={refetch} />}
          exact
        />
        <DashRoute
          route="partner-scoped"
          render={() => (
            <>
              <PartnerScopedNavBar isNavVisible={isNavVisible} />
              <AttachPartnerAnalyticsMetadata />
              <UserFeedbackButton />
              <div
                css={{
                  display: 'flex',

                  // this CSS is a relic of the Nav V1 implementation. If we don't position absolute left: 0
                  // the content is shifted off screen to the left.
                  position: 'absolute',
                  left: leftPadding,

                  width: `calc(100% - ${leftPadding})`,
                  minHeight: `calc(100% - ${topPadding})`,
                  height: `calc(100% - ${topPadding})`,
                }}
              >
                {isNavVisible && isSideNavVisible && isWide && <LeftShortcutsPaneRetailer />}

                <PartnerScopedFeatureEducation pathname={pathname} />

                <AiPageLayout>
                  <AppContentContainer css={{ flex: 1, minWidth: 0 }}>
                    <ContactHeader />
                    <ErrorBoundary pathname={pathname}>
                      <DashSwitch>
                        <DashRoute route="retailer-scoped" component={RetailerScopedSection} />
                        <PartnerScopedRoutes />
                      </DashSwitch>
                    </ErrorBoundary>
                  </AppContentContainer>
                </AiPageLayout>
              </div>
            </>
          )}
        />
      </DashSwitch>
    </>
  )
}
